import type { DeckLang } from "@/types/linguadeck";

export type RelationType =
  | "derivative"
  | "antonym"
  | "synonym"
  | "compound"
  | "inflection"
  | "cognate";

export type PartOfSpeech =
  | "noun"
  | "verb"
  | "adjective"
  | "adverb"
  | "preposition"
  | "pronoun"
  | "conjunction"
  | "interjection"
  | "article"
  | "particle"
  | "numeral"
  | "phrase"
  | "other";

export const RELATION_ORDER: RelationType[] = [
  "derivative",
  "synonym",
  "antonym",
  "compound",
  "inflection",
  "cognate",
];

export const ALLOWED_RELATIONS_BY_LANG: Record<DeckLang, RelationType[]> = {
  en: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  es: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  fr: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  de: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  it: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  nl: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  pl: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  "pt-BR": ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  ru: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  uk: ["derivative", "antonym", "synonym", "compound", "inflection", "cognate"],
  tr: ["derivative", "synonym", "antonym", "inflection", "compound"],
  ko: ["derivative", "synonym", "antonym", "compound"],
};

export const POS_COLORS: Record<PartOfSpeech, { bg: string; text: string; ring: string }> = {
  noun:        { bg: "bg-sky-50 dark:bg-sky-500/15",        text: "text-sky-700 dark:text-sky-300",        ring: "ring-sky-200 dark:ring-sky-700" },
  verb:        { bg: "bg-emerald-50 dark:bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-200 dark:ring-emerald-700" },
  adjective:   { bg: "bg-amber-50 dark:bg-amber-500/15",     text: "text-amber-700 dark:text-amber-300",    ring: "ring-amber-200 dark:ring-amber-700" },
  adverb:      { bg: "bg-violet-50 dark:bg-violet-500/15",   text: "text-violet-700 dark:text-violet-300",  ring: "ring-violet-200 dark:ring-violet-700" },
  preposition: { bg: "bg-rose-50 dark:bg-rose-500/15",       text: "text-rose-700 dark:text-rose-300",      ring: "ring-rose-200 dark:ring-rose-700" },
  pronoun:     { bg: "bg-fuchsia-50 dark:bg-fuchsia-500/15", text: "text-fuchsia-700 dark:text-fuchsia-300", ring: "ring-fuchsia-200 dark:ring-fuchsia-700" },
  conjunction: { bg: "bg-cyan-50 dark:bg-cyan-500/15",       text: "text-cyan-700 dark:text-cyan-300",      ring: "ring-cyan-200 dark:ring-cyan-700" },
  interjection:{ bg: "bg-pink-50 dark:bg-pink-500/15",       text: "text-pink-700 dark:text-pink-300",      ring: "ring-pink-200 dark:ring-pink-700" },
  article:     { bg: "bg-slate-50 dark:bg-slate-500/15",     text: "text-slate-700 dark:text-slate-300",    ring: "ring-slate-200 dark:ring-slate-700" },
  particle:    { bg: "bg-stone-50 dark:bg-stone-500/15",     text: "text-stone-700 dark:text-stone-300",    ring: "ring-stone-200 dark:ring-stone-700" },
  numeral:     { bg: "bg-indigo-50 dark:bg-indigo-500/15",   text: "text-indigo-700 dark:text-indigo-300",  ring: "ring-indigo-200 dark:ring-indigo-700" },
  phrase:      { bg: "bg-teal-50 dark:bg-teal-500/15",       text: "text-teal-700 dark:text-teal-300",      ring: "ring-teal-200 dark:ring-teal-700" },
  other:       { bg: "bg-gray-50 dark:bg-gray-500/15",       text: "text-gray-700 dark:text-gray-300",      ring: "ring-gray-200 dark:ring-gray-700" },
};

export function normalizePartOfSpeech(raw: string | null | undefined): PartOfSpeech {
  if (!raw) return "other";
  const v = raw.trim().toLowerCase();
  switch (v) {
    case "noun": case "n.": case "n": return "noun";
    case "verb": case "v.": case "v": return "verb";
    case "adjective": case "adj.": case "adj": return "adjective";
    case "adverb": case "adv.": case "adv": return "adverb";
    case "preposition": case "prep.": case "prep": return "preposition";
    case "pronoun": case "pron.": case "pron": return "pronoun";
    case "conjunction": case "conj.": case "conj": return "conjunction";
    case "interjection": case "interj.": case "interj": return "interjection";
    case "article": case "art.": return "article";
    case "particle": case "part.": return "particle";
    case "numeral": case "num.": return "numeral";
    case "phrase": case "ph.": return "phrase";
    default: return "other";
  }
}

export function isValidRelationType(s: unknown): s is RelationType {
  return s === "derivative" || s === "antonym" || s === "synonym" ||
         s === "compound" || s === "inflection" || s === "cognate";
}
